import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, CardBody, CardTitle, CardSubtitle, Button, Spinner } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import DetailBloc from './Blocs/DetailBloc';
import apiResponse from '../../../../services/apiResponse';
import DetailHeaderCard from './Components/DetailHeaderCard';
import EvaluatorGroupTable from './Components/EvaluatorGroupTable';
import RoleWeightingTable from './Components/RoleWeightingTable';
import BerakhlakWeightingTable from './Components/BerakhlakWeightingTable';
import { sampleDetailBreakdown, employeeList360 } from '../mockData';
import '../custom360.css';

const DetailView = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const uuid = queryParams.get('uuid') || queryParams.get('detail');

  const detailBloc = useMemo(() => new DetailBloc(), []);
  const [detailData, setDetailData] = useState(sampleDetailBreakdown);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const subscription = detailBloc.detailChannel.subscribe((result) => {
      switch (result.status) {
        case apiResponse.LOADING:
          setLoading(true);
          break;
        case apiResponse.COMPLETED:
          setLoading(false);
          const response = result.data?.response || result.data?.data || result.data || {};
          const assessment = response.assessment || {};
          const stats = response.stats || {};

          setDetailData((prev) => {
            const base = prev || sampleDetailBreakdown;
            return {
              ...base,
              assessment,
              stats,
              employee: {
                ...base.employee,
                nama: assessment.ms360_team_leader_name || base.employee?.nama || '-',
                jabatan: assessment.ms360_team_name || base.employee?.jabatan || '-',
                unitKerja: assessment.ms360_team_work_unit_name || base.employee?.unitKerja || '-',
              },
              scores: {
                ...base.scores,
                nilaiKeseluruhan:
                  stats.nilai_rata_rata !== undefined && stats.nilai_rata_rata !== null
                    ? Number(stats.nilai_rata_rata).toFixed(1)
                    : base.scores?.nilaiKeseluruhan || '0.0',
              },
              totalMapping: stats.total_penilai || base.totalMapping || 13,
            };
          });
          break;
        case apiResponse.ERROR:
          setLoading(false);
          break;
        default:
          break;
      }
    });

    if (uuid && uuid !== '1' && uuid !== 'true') {
      // Fetch detail from Transaction/Recap360/detail with param { uuid }
      detailBloc.fetchDetail({ uuid });

      const foundEmp = employeeList360.find((emp) => emp.uuid === uuid);
      if (foundEmp) {
        setDetailData({
          ...sampleDetailBreakdown,
          employee: {
            nama: foundEmp.nama,
            nip: foundEmp.nip,
            jabatan: foundEmp.jabatan,
            unitKerja: foundEmp.unitKerja,
            level: foundEmp.level || 'Level 4',
          },
          scores: {
            nilaiKeseluruhan: foundEmp.nilaiAkhir360 !== '-' ? foundEmp.nilaiAkhir360 : '0.0',
            predikatKeseluruhan: foundEmp.predikat !== '-' ? foundEmp.predikat : 'Sangat Kurang',
            predikatKeseluruhanId: foundEmp.predikatId || 'sangat-kurang',
            berAkhlak360: foundEmp.nilaiBerAkhlak !== '-' ? foundEmp.nilaiBerAkhlak : '0.0',
            predikatBerAkhlak: foundEmp.predikat !== '-' ? foundEmp.predikat : 'Sangat Kurang',
            predikatBerAkhlakId: foundEmp.predikatId || 'sangat-kurang',
          }
        });
      }
    }

    return () => {
      subscription.unsubscribe();
    };
  }, [detailBloc, uuid]);

  const employeeName = detailData?.employee?.nama || 'Alvian Yusuf Mizan, A.Md.Ak.';

  return (
    <Fragment>
      <Container fluid>
        <Row>
          <Col xs="12">
            <Card className="border-0 shadow-sm" style={{ borderRadius: '14px' }}>
              <CardBody className="p-4">
                {/* Top Action Header */}
                <div className="float-end">
                  <Link to="/kepegawaian/rekap360.html" className="waves-effect">
                    <Button color="danger" size="sm">
                      <i className="uil-arrow-left me-1"></i> Kembali
                    </Button>
                  </Link>
                </div>
                <CardTitle className="h4 mb-1">Detail Breakdown Umpan Balik 360°</CardTitle>
                <CardSubtitle className="text-muted mb-3">
                  Rincian skor per individu penilai dan tabel kalkulasi pembobotan akhir untuk <strong>{employeeName}</strong>.
                </CardSubtitle>
                <hr />

                {/* Content Breakdown or Loading */}
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <div className="text-muted mt-2 small">Memuat detail rekap 360°...</div>
                  </div>
                ) : (
                  <>
                    {/* Header Profile & Scores */}
                    <DetailHeaderCard data={detailData} />

                    {/* Evaluator Groups Table */}
                    <EvaluatorGroupTable
                      evaluatorGroups={detailData.evaluatorGroups}
                      totalMapping={detailData.totalMapping || 13}
                    />

                    {/* Role Weighting Table */}
                    <RoleWeightingTable
                      roleWeightings={detailData.roleWeightings}
                      totalWeighting={detailData.totalWeighting}
                    />

                    {/* Core Values BerAKHLAK Weighting Table */}
                    <BerakhlakWeightingTable
                      berakhlakWeightings={detailData.berakhlakWeightings}
                      berakhlakSummary={detailData.berakhlakSummary}
                      totalMapping={detailData.totalMapping || 13}
                    />
                  </>
                )}

                {/* Bottom Back Button matching standard baseui detail pattern */}
                <hr />
                <Row className="mt-3">
                  <Col md="12">
                    <Link to="/kepegawaian/rekap360.html" className="waves-effect">
                      <Button color="danger">
                        <i className="uil-arrow-left me-1"></i> Kembali
                      </Button>
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default DetailView;
